/**
 * Auth Redux Slice
 * Manages authentication state (Firebase UID, JWT token, user data)
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this slice only stores authentication state.
 * All authentication logic is handled by Firebase and backend services.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../types/user';

export interface AuthState {
  firebase_uid: string | null;
  jwt_token: string | null;
  user: User | null;
  authenticated: boolean;
  loading: boolean;
  error: string | null;
  rememberMe: boolean;
  initialized: boolean;
}

const initialState: AuthState = {
  firebase_uid: null,
  jwt_token: null,
  user: null,
  authenticated: false,
  loading: false,
  error: null,
  rememberMe: false,
  initialized: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<{ firebase_uid: string; jwt_token: string; rememberMe?: boolean }>) => {
      state.firebase_uid = action.payload.firebase_uid;
      state.jwt_token = action.payload.jwt_token;
      state.authenticated = true;
      state.error = null;
      state.rememberMe = action.payload.rememberMe || false;
      state.initialized = true;
      
      // Store token and Firebase UID in localStorage (web) or secure storage (mobile)
      if (typeof window !== 'undefined') {
        if (action.payload.rememberMe) {
          localStorage.setItem('flowrithm_jwt_token', action.payload.jwt_token);
          localStorage.setItem('flowrithm_firebase_uid', action.payload.firebase_uid);
          localStorage.setItem('flowrithm_remember_me', 'true');
        } else {
          // Use sessionStorage for non-remember me sessions
          sessionStorage.setItem('flowrithm_jwt_token', action.payload.jwt_token);
          sessionStorage.setItem('flowrithm_firebase_uid', action.payload.firebase_uid);
        }
      }
      // TODO: For mobile, use secure storage
    },
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload;
    },
    logout: (state) => {
      state.firebase_uid = null;
      state.jwt_token = null;
      state.user = null;
      state.authenticated = false;
      state.error = null;
      state.rememberMe = false;
      state.initialized = true;
      
      // Clear token and Firebase UID from storage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('flowrithm_jwt_token');
        localStorage.removeItem('flowrithm_firebase_uid');
        localStorage.removeItem('flowrithm_remember_me');
        sessionStorage.removeItem('flowrithm_jwt_token');
        sessionStorage.removeItem('flowrithm_firebase_uid');
      }
      // TODO: For mobile, clear secure storage
    },
  },
});

export const { setAuth, setUser, setLoading, setError, setRememberMe, setInitialized, logout } = authSlice.actions;
export default authSlice.reducer;

